import React, { useRef, useState } from 'react';
import { onSubmitProps } from '../../../Constants/constants';
import { useParams } from 'react-router-dom';

import Helper from '../../../Utils/Helpers';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Column, Query, QueryResult } from 'material-table';
import { IconButton } from '@material-ui/core';
import LinkOffOutlinedIcon from '@material-ui/icons/LinkOffOutlined';
import { IOverrideEntityActions } from '../../../API/Interfaces/IEntityActions';
import ISearchBaseDto from '../../../API/Interfaces/ISearchBaseDto';
import ConfirmModal, { IConfirmModal } from '../../../Layout/Components/ConfirmModal';
import EntityPage from '../../../Layout/EntityPage';
import LocationsServiceAPI from '../../../API/Services/LocationServiceAPI';
import { IBaseRuleItemDto, IRuleDetailsDto } from '../../../API/Interfaces/IRuleDtos';
import ConnectRuleForm from './ConnectRuleForm';
import RuleServiceAPI from '../../../API/Services/RuleServiceAPI';
import RuleForm from '../../Rule/RuleForm';
import NoRedirectingLink from '../../../Layout/Components/NoRedirectingLink';
import Modal from '../../../Layout/Components/Modal';

interface IConfirmPopup {
	id?: number;
	isVisible: boolean;
	text: string;
}

const LocationRules = () => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const tableRef = useRef();
	const [isConnectModalVisible, setIsConnectModalVisible] = useState(false);
	const { id } = useParams<{ id: string }>();
	const [entity, setEntity] = useState<IRuleDetailsDto>();
	const [open, setOpen] = useState(false);
	const [popupConfirmShown, setPopupConfirmShown] = useState<IConfirmPopup>();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const savedDialog = (message: string) => enqueueSnackbar(message, { variant: 'success' });

	const headers: Array<Column<IBaseRuleItemDto>> = [
		{
			title: t('locations.table.nameHeader'),
			field: 'title',
			defaultSort: 'asc',
			render: (rowData) => {
				return (
					<NoRedirectingLink
						onClick={async () => {
							const response = await fetchRule(rowData.id);
							if (response) {
								setEntity(response);
								setOpen(true);
							}
						}}
					>
						<span>{rowData.title}</span>
					</NoRedirectingLink>
				);
			},
		},
		{
			title: t('common.table.unlinkHeader'),
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) => (
				<IconButton
					onClick={() =>
						setPopupConfirmShown({
							isVisible: true,
							id: rowData.id,
							text: t('location.removeConfirm', { goal: rowData.title }),
						})
					}
					style={{ marginLeft: 'auto', display: 'block' }}
				>
					<LinkOffOutlinedIcon style={{ color: 'black' }} />
				</IconButton>
			),
		},
	];

	const EntityActions: IOverrideEntityActions<IRuleDetailsDto, IBaseRuleItemDto> = {
		api: LocationsServiceAPI,
		presetDataCallback: (data: any) => {
			return data;
		},
		setModalOpen: (e) => setIsConnectModalVisible(e),
		getTableData: async function (query: Query<IBaseRuleItemDto>) {
			const result: QueryResult<IBaseRuleItemDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetLocationRules(id, request);

				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;

					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { paging: true },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('menu.rules'),
		tableProps: tableProps,
		isDetails: false, //props.isInDetails,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const disconnectRule = async () => {
		if (popupConfirmShown && popupConfirmShown.id && id) {
			Helper.setLoader(true);

			const response = await LocationsServiceAPI.DisconnectRuleFromLocation(id, popupConfirmShown!.id);
			if (response) {
				Helper.setLoader(false);
				if (response === 1) {
					savedDialog(t('location.removeRuleInfo'));
					setPopupConfirmShown({ id: undefined, text: '', isVisible: false });
					refreshTable();
				} else {
					errorDialog();
				}
			}
		}
	};

	const handleConfirmationModalProps: IConfirmModal = {
		handleOperation: disconnectRule,
		isOpen: popupConfirmShown?.isVisible ?? false,
		handleClose: () => setPopupConfirmShown({ id: undefined, text: '', isVisible: false }),
		text: popupConfirmShown?.text ?? '',
		variant: 'outlined',
	};

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen(e),
		api: RuleServiceAPI,
		refreshTable: refreshTable,
		setEntity: () => refreshTable(),
	};

	const fetchRule = async function (id: number) {
		try {
			const response = await RuleServiceAPI.GetOne(id);
			if (response) return response;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	return (
		<>
			{open && (
				<Modal
					form={<RuleForm />}
					isOpen={open}
					callbacks={callbacks}
					entity={entity}
					modalTitle={t('repository.rules.editRule')}
					//onlyCancelButton
					//blueTheme
				/>
			)}
			{isConnectModalVisible && (
				<ConnectRuleForm
					onClose={() => setIsConnectModalVisible(false)}
					isOpen={isConnectModalVisible}
					onRefresh={() => refreshTable()}
				/>
			)}

			{popupConfirmShown?.isVisible && <ConfirmModal {...handleConfirmationModalProps} />}
			<EntityPage {...entityPageProps} />
		</>
	);
};

export default LocationRules;
